import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { catchError, map } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { ShipmentPlannerProfileItemService } from './shipment-planner-profile-item.service';
import {
  ShipmentPlannerProfileItemCreate,
  ShipmentPlannerProfileItemInitialState,
  ShipmentPlannerProfileItemLoad,
  ShipmentPlannerProfileItemUpdate,
  ShipmentPlannerDefaultProfileItemLoad,
  ShipmentPlannerDefaultProfileItemSetToggle,
  ShipmentPlannerProfileItemReset,
} from './shipment-planner-profile-item.actions';
import { ShipmentPlannerProfileModel } from '../models';
import { Error, CustomHttpErrorResponse } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ShipmentPlannerProfileItemStateModel {
  profile: ShipmentPlannerProfileModel | null;
  loaded: boolean;
}

const initialState: ShipmentPlannerProfileItemStateModel = {
  profile: null,
  loaded: false,
};

@State<ShipmentPlannerProfileItemStateModel>({
  name: 'shipmentPlannerProfileItem',
  defaults: initialState,
})
@Injectable()
export class ShipmentPlannerProfileItemState {
  constructor(
    private shipmentPlannerProfileItemService: ShipmentPlannerProfileItemService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ShipmentPlannerProfileItemStateModel): ShipmentPlannerProfileItemStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: ShipmentPlannerProfileItemStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getProfileId(state: ShipmentPlannerProfileItemStateModel): number {
    return state.profile?.id;
  }

  @Selector()
  static getProfile(state: ShipmentPlannerProfileItemStateModel): ShipmentPlannerProfileModel {
    return state.profile;
  }

  @Action(ShipmentPlannerDefaultProfileItemLoad)
  getShipmentProfileItemDefaultProfile(
    ctx: StateContext<ShipmentPlannerProfileItemStateModel>,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentPlannerProfileItemService.getShipmentDefaultProfile().pipe(
      map((profile: ShipmentPlannerProfileModel) => {
        ctx.patchState({ profile, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerDefaultProfileItemSetToggle)
  setShipmentProfileItemDefaultProfileToggle(
    ctx: StateContext<ShipmentPlannerProfileItemStateModel>,
    { payload }: ShipmentPlannerDefaultProfileItemSetToggle,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentPlannerProfileItemService.setShipmentProfileItemDefaultProfileToggle(payload).pipe(
      map(() => {
        ctx.patchState({ profile: { ...ctx.getState().profile, isDefault: payload.isDefault } });
        this.notificationService.success('api.shipmentProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemLoad)
  getShipmentPlannerProfileItem(
    ctx: StateContext<ShipmentPlannerProfileItemStateModel>,
    { payload }: ShipmentPlannerProfileItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentPlannerProfileItemService.getShipmentPlannerProfile(payload).pipe(
      map((shipmentPlannerProfile: ShipmentPlannerProfileModel) => {
        ctx.patchState({
          profile: new ShipmentPlannerProfileModel(shipmentPlannerProfile),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemCreate)
  createShipmentPlannerProfileItem(
    ctx: StateContext<ShipmentPlannerProfileItemStateModel>,
    { payload }: ShipmentPlannerProfileItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    ctx.patchState({ loaded: false });

    return this.shipmentPlannerProfileItemService.createShipmentPlannerProfile(payload.profile).pipe(
      map((profile: ShipmentPlannerProfileModel) => {
        ctx.patchState({ profile, loaded: true });
        payload.dialog.close({ id: profile.id, name: profile.name, isDefault: profile.isDefault });
        this.notificationService.success('api.shipmentProfile.success.create');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemUpdate)
  updateShipmentPlannerProfileItem(
    ctx: StateContext<ShipmentPlannerProfileItemStateModel>,
    { payload }: ShipmentPlannerProfileItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.shipmentPlannerProfileItemService.updateShipmentPlannerProfile(payload).pipe(
      map(() => {
        this.notificationService.success('api.shipmentProfile.success.update');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ShipmentPlannerProfileItemReset)
  resetShipmentPlannerProfileItem(ctx: StateContext<ShipmentPlannerProfileItemStateModel>): void {
    ctx.patchState({ profile: null });
  }

  @Action(ShipmentPlannerProfileItemInitialState)
  setInitialState(ctx: StateContext<ShipmentPlannerProfileItemStateModel>): void {
    ctx.setState(initialState);
  }
}
